import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FavoriteCard } from '@/components/shared/FavoriteCard';
import { Heart } from 'lucide-react';

interface UserFavoritesTabProps {
  userId: string;
}

interface Favorite {
  id: string;
  created_at: string;
  service_id: number;
  services?: {
    id: number;
    name: string;
    location?: string;
    price?: number;
    unit?: string;
    rating?: number;
    published: boolean;
    image_url?: string;
  };
}

const UserFavoritesTab: React.FC<UserFavoritesTabProps> = ({ userId }) => {
  const { data: favorites, isLoading } = useQuery({
    queryKey: ['user-favorites', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('favorites')
        .select(`
          id,
          created_at,
          service_id,
          services (
            id,
            name,
            location,
            price,
            unit,
            rating,
            published,
            image_url
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Favorite[];
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (!favorites || favorites.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Heart className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No favorites found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {favorites.map((favorite) => (
        <FavoriteCard
          key={favorite.id}
          favorite={favorite}
          showPublishStatus={true}
        />
      ))}
    </div>
  );
};

export default UserFavoritesTab;